import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Globe, RefreshCw, ListChecks, FileSearch, Settings, Lock,
  Search, Mail, Shield, EyeOff, KeyRound, Bell, Zap, Workflow,
  ServerCog, Network, ShieldCheck,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "domain-registration": {
    eyebrow: "Domains",
    title: "Domain Registration",
    sub: "Register 500+ TLDs with free privacy, DNS and email forwarding.",
    metaDesc: "Domain registration across 500+ TLDs with free WHOIS privacy, DNS, email forwarding and auto-renewal.",
    intro:
      "Lock in your brand with the right domain. We're an ICANN-accredited registrar offering 500+ TLDs at transparent prices — every registration includes free WHOIS privacy, anycast DNS, email forwarding and one-click SSL.",
    features: [
      { icon: Globe, title: "500+ TLDs", description: ".com, .ai, .io, .xyz, country-code and brand TLDs." },
      { icon: Lock, title: "Free privacy", description: "WHOIS privacy free for life — never upsold later." },
      { icon: Settings, title: "Free DNS", description: "Anycast DNS with full record-type support." },
      { icon: Mail, title: "Email forwarding", description: "Unlimited aliases forwarded to your inbox." },
      { icon: Shield, title: "Registrar lock + 2FA", description: "Prevents unauthorized transfers and changes." },
      { icon: Bell, title: "Auto-renewal", description: "Reminders + auto-renew so you never lose a domain." },
    ],
    process: [
      { step: "01", title: "Search", description: "AI-suggested names and TLD alternatives." },
      { step: "02", title: "Register", description: "1-click checkout with privacy included." },
      { step: "03", title: "Configure", description: "DNS, email forwarding, SSL — done in minutes." },
      { step: "04", title: "Manage", description: "Renewals and DNS from one dashboard." },
    ],
    deliverables: ["Registered domain", "WHOIS privacy", "Anycast DNS", "Email forwarding", "2FA + registrar lock"],
    techStack: ["ICANN-accredited", "Anycast DNS", "DNSSEC", "Let's Encrypt"],
    pricing: [
      { name: ".com / .net", price: "$9.99", period: "year", features: ["Free privacy", "Free DNS", "Email forwarding"] },
      { name: ".ai / .io", price: "$59", period: "year", highlighted: true, features: ["Premium TLDs", "Free privacy + DNS", "Priority support"] },
      { name: "Country / brand", price: "From $25", period: "year", features: ["ccTLDs", "Brand TLDs", "Compliance assist"] },
    ],
    faqs: [
      { q: "Do I really own the domain?", a: "Yes — you're listed as the registrant; we're only the registrar of record." },
      { q: "Can I bulk-register?", a: "Yes — bulk search and bulk checkout for up to 500 domains at once." },
    ],
  },
  "domain-transfer": {
    eyebrow: "Domains",
    title: "Domain Transfer",
    sub: "Move domains in or out — fast, free and lock-free.",
    metaDesc: "Transfer domains in or out with no transfer fees, no lock-in and one extra year of renewal included.",
    intro:
      "Bring your domain home. Transfer in from any registrar in 5–7 days with no transfer fee and a bonus year of renewal included. Bulk transfers, white-glove migration and DNS continuity guaranteed.",
    features: [
      { icon: RefreshCw, title: "No transfer fees", description: "We don't charge to transfer — and you get +1 year." },
      { icon: Workflow, title: "Bulk transfers", description: "Move hundreds of domains in one batch with CSV import." },
      { icon: Shield, title: "DNS continuity", description: "We mirror your DNS so sites stay up during transfer." },
      { icon: KeyRound, title: "EPP code handling", description: "We guide you through unlocking and auth codes." },
      { icon: Bell, title: "Transfer alerts", description: "Email updates at every stage of the EPP handoff." },
      { icon: Lock, title: "Lock-free policy", description: "Transfer out anytime — no hostage tactics." },
    ],
    process: [
      { step: "01", title: "Unlock", description: "Unlock at current registrar and get EPP code." },
      { step: "02", title: "Initiate", description: "Paste code, confirm DNS, start transfer." },
      { step: "03", title: "Approve", description: "Approve via admin email or registrar dashboard." },
      { step: "04", title: "Land", description: "Domain lands with +1 year added." },
    ],
    deliverables: ["Transfer-in setup", "DNS mirroring", "EPP handling", "Status alerts", "+1 year renewal"],
    techStack: ["EPP", "RDAP", "Anycast DNS", "Bulk CSV import"],
    pricing: [
      { name: "Single", price: "$9.99", period: "transfer", features: ["+1 year renewal", "Free privacy", "DNS mirroring"] },
      { name: "Bulk (10+)", price: "$8.49", period: "transfer", highlighted: true, features: ["CSV import", "Concierge tracking", "Free privacy"] },
      { name: "Enterprise", price: "Custom", features: ["1000+ domains", "Dedicated PM", "Brand protection audit"] },
    ],
    faqs: [
      { q: "How long does transfer take?", a: "5–7 days for most TLDs; some ccTLDs vary. We track and alert at every stage." },
      { q: "Will my site go down?", a: "No — we mirror your DNS before the transfer so resolution is uninterrupted." },
    ],
  },
  "bulk-search": {
    eyebrow: "Domains",
    title: "Bulk Search",
    sub: "Check hundreds of domain ideas in seconds.",
    metaDesc: "Bulk domain search across 500+ TLDs with availability, pricing and AI-powered name suggestions.",
    intro:
      "Brainstorming a brand? Paste up to 500 names and we'll check availability across every TLD instantly — plus AI suggestions for variations, synonyms and alternative endings.",
    features: [
      { icon: ListChecks, title: "Bulk lookup", description: "Up to 500 names × all TLDs at once." },
      { icon: Search, title: "AI suggestions", description: "Synonyms, prefixes, suffixes and TLD swaps." },
      { icon: Zap, title: "Instant results", description: "Sub-second responses with live pricing." },
      { icon: Workflow, title: "CSV export", description: "Download results for review and stakeholder sign-off." },
      { icon: Lock, title: "Saved searches", description: "Pin candidates and revisit availability over time." },
      { icon: Bell, title: "Drop-catch alerts", description: "Get notified when a target domain frees up." },
    ],
    process: [
      { step: "01", title: "Paste", description: "Drop in your name list or upload a CSV." },
      { step: "02", title: "Check", description: "Run bulk availability across selected TLDs." },
      { step: "03", title: "Refine", description: "AI variants + manual filters." },
      { step: "04", title: "Register", description: "1-click bulk checkout." },
    ],
    deliverables: ["Bulk availability report", "AI suggestions", "Saved candidate list", "Drop-catch monitors"],
    techStack: ["RDAP", "Internal pricing API", "AI naming model"],
    pricing: [
      { name: "Free", price: "$0", features: ["50 names / day", "Top TLDs", "CSV export"] },
      { name: "Pro", price: "$15", period: "month", highlighted: true, features: ["Unlimited names", "All TLDs", "Drop-catch alerts"] },
      { name: "Agency", price: "$60", period: "month", features: ["Team seats", "Brand monitoring", "API access"] },
    ],
    faqs: [
      { q: "Do you support drop-catching?", a: "Yes — we monitor target domains and attempt registration the moment they drop." },
      { q: "Is there an API?", a: "Yes — Agency tier includes API access for automation." },
    ],
  },
  "whois-lookup": {
    eyebrow: "Domains · Tools",
    title: "WHOIS Lookup",
    sub: "Free WHOIS / RDAP lookups across every TLD.",
    metaDesc: "Free WHOIS and RDAP lookup tool for every TLD — registrant, registrar, expiry, name servers and DNSSEC status.",
    intro:
      "Look up registrant, registrar, expiry and name servers for any domain — using modern RDAP where available with WHOIS fallback. Bulk lookups, history and monitoring for the names that matter.",
    features: [
      { icon: FileSearch, title: "RDAP + WHOIS", description: "Modern RDAP queries with WHOIS fallback." },
      { icon: ListChecks, title: "Bulk lookup", description: "Up to 1000 domains per query." },
      { icon: Bell, title: "Expiry monitoring", description: "Alerts before tracked domains expire." },
      { icon: Workflow, title: "History", description: "Track WHOIS changes over time." },
      { icon: Network, title: "DNS + DNSSEC", description: "Name servers, DNSSEC status and SOA serial." },
      { icon: Lock, title: "Privacy-aware", description: "Respects GDPR-redacted records cleanly." },
    ],
    process: [
      { step: "01", title: "Query", description: "Paste domain or upload list." },
      { step: "02", title: "Fetch", description: "RDAP first, WHOIS fallback." },
      { step: "03", title: "Compare", description: "Diff against historical snapshots." },
      { step: "04", title: "Monitor", description: "Subscribe to change alerts." },
    ],
    deliverables: ["Lookup report", "Bulk results CSV", "Change history", "Expiry alerts"],
    techStack: ["RDAP", "WHOIS port 43", "Anycast resolvers"],
    pricing: [
      { name: "Free", price: "$0", features: ["100 lookups / day", "Single domain", "Public history"] },
      { name: "Pro", price: "$9", period: "month", highlighted: true, features: ["10k lookups / mo", "Bulk + CSV", "Expiry alerts"] },
      { name: "API", price: "$49", period: "month", features: ["100k API calls", "Webhook alerts", "Team seats"] },
    ],
    faqs: [
      { q: "Why is contact info redacted?", a: "GDPR and registry policies redact personal data — we show whatever the registry publishes." },
      { q: "Do you support ccTLDs?", a: "Yes — every TLD that exposes WHOIS or RDAP is supported." },
    ],
  },
  "dns-management": {
    eyebrow: "Domains · Tools",
    title: "DNS Management",
    sub: "Anycast DNS with full record support, DNSSEC and analytics.",
    metaDesc: "Managed DNS on a global anycast network with DNSSEC, full record types, traffic steering and analytics.",
    intro:
      "Run your DNS on a global anycast network with sub-30ms resolution from anywhere. Full record-type support (A/AAAA/CNAME/MX/TXT/SRV/CAA/ALIAS), DNSSEC, geo-steering, weighted routing and per-query analytics.",
    features: [
      { icon: Network, title: "Global anycast", description: "200+ PoPs for sub-30ms resolution." },
      { icon: ShieldCheck, title: "DNSSEC", description: "One-click DNSSEC with auto key rotation." },
      { icon: Workflow, title: "Traffic steering", description: "Geo, weighted and health-based routing." },
      { icon: Settings, title: "All record types", description: "A, AAAA, CNAME, MX, TXT, SRV, CAA, ALIAS, SVCB/HTTPS." },
      { icon: Zap, title: "Instant propagation", description: "Edge cache invalidated globally in seconds." },
      { icon: ServerCog, title: "API + Terraform", description: "Manage records as code." },
    ],
    process: [
      { step: "01", title: "Add zone", description: "Import via AXFR or paste zone file." },
      { step: "02", title: "Cutover", description: "Update name servers at registrar." },
      { step: "03", title: "Secure", description: "Enable DNSSEC and CAA." },
      { step: "04", title: "Optimize", description: "Add geo steering and health checks." },
    ],
    deliverables: ["DNS zone", "DNSSEC enabled", "Traffic policies", "API access", "Query analytics"],
    techStack: ["Anycast DNS", "DNSSEC", "Terraform provider", "AXFR import"],
    pricing: [
      { name: "Free", price: "$0", features: ["1 zone", "Standard records", "Anycast"] },
      { name: "Pro", price: "$10", period: "month", highlighted: true, features: ["50 zones", "DNSSEC", "Traffic steering"] },
      { name: "Enterprise", price: "Custom", features: ["Unlimited zones", "SLA 100%", "Dedicated PoPs"] },
    ],
    faqs: [
      { q: "Can I keep my domain at another registrar?", a: "Yes — just point name servers to us." },
      { q: "Do you support ALIAS / ANAME?", a: "Yes — flatten CNAME at the apex for root domains." },
    ],
  },
  "domain-privacy": {
    eyebrow: "Domains · Tools",
    title: "Domain Privacy",
    sub: "Free WHOIS privacy that keeps your contact info hidden — for life.",
    metaDesc: "Free WHOIS privacy protection that masks registrant contact details with proxy data — for life, on every TLD.",
    intro:
      "Public WHOIS exposes your name, address, phone and email to spammers, scammers and lawsuits. Our free WHOIS privacy replaces your details with proxy contact info and forwards legitimate inquiries — at no extra cost, for the life of the domain.",
    features: [
      { icon: EyeOff, title: "Identity masking", description: "Proxy contact info shown in WHOIS / RDAP." },
      { icon: Mail, title: "Inquiry forwarding", description: "Legitimate emails forwarded to you, spam filtered." },
      { icon: Shield, title: "GDPR-aligned", description: "Compliant redaction across EU and global TLDs." },
      { icon: Lock, title: "Always free", description: "No upsell, no expiry — included for life." },
      { icon: Workflow, title: "Bulk toggle", description: "Enable across hundreds of domains in one click." },
      { icon: Bell, title: "Disclosure controls", description: "Choose exactly when contact is revealed." },
    ],
    process: [
      { step: "01", title: "Enable", description: "On by default for supported TLDs." },
      { step: "02", title: "Verify", description: "Confirm forwarding email and preferences." },
      { step: "03", title: "Mask", description: "WHOIS records updated within hours." },
      { step: "04", title: "Manage", description: "Bulk toggle and disclosure rules." },
    ],
    deliverables: ["Privacy enabled", "Inquiry forwarder", "Spam filter", "Bulk privacy console"],
    techStack: ["Proxy contact service", "Anti-spam", "RDAP / WHOIS"],
    pricing: [
      { name: "Free", price: "$0", features: ["All supported TLDs", "Inquiry forwarding", "Spam filter"] },
      { name: "Pro", price: "$3", period: "year", highlighted: true, features: ["Custom forward rules", "Multiple inboxes", "Disclosure controls"] },
      { name: "Enterprise", price: "Custom", features: ["Brand-controlled proxy", "Legal escalation", "Audit logs"] },
    ],
    faqs: [
      { q: "Does privacy hurt SEO?", a: "No — search engines do not factor WHOIS into rankings." },
      { q: "Which TLDs support it?", a: "Most gTLDs and many ccTLDs; the dashboard shows status per domain." },
    ],
  },
};

export const Route = createFileRoute("/hosting/domains/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/hosting/domains/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — Domains | CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — Domains | CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: DomainsChild,
});

function DomainsChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
